import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseMarketSectorCorrelation {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private xAxisLabels: string[] = ['XLB', 'XLC', 'XLE', 'XLF', 'XLI', 'XLK', 'XLP', 'XLRE', 'XLU', 'XLV', 'XLY'];
    private yAxisLabels: string[] = ['XLY', 'XLV', 'XLU', 'XLRE', 'XLP', 'XLK', 'XLI', 'XLF', 'XLE', 'XLC', 'XLB'];

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Sector Correlation</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow" data-testing-id="options-row">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01" data-testing-id="symbol-selector">
                <div class="trader-sm-card" id="selected-symbol" data-testing-id="selected-symbol-card">
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
              <span></span>
            </div>

            <div class="chartDv" id="heatMapContainer" style="height: calc(${this.config.height});" data-testing-id="heatmap-container">
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        try {
            const endDate = new Date();
            const startDate = new Date(endDate);
            startDate.setFullYear(endDate.getFullYear() - 1);
            const fmt = (d: Date) => d.toISOString().split('T')[0];

            const payload = {
                start_date: fmt(startDate),
                end_date: fmt(endDate),
                interval: "daily"
            };
            const response = await apiGet({
                url: `${APIURL}/v1/performance/corelation`,
                body: payload,
            });

            if (!response.ok) {
                console.error(`API error: ${response.status} ${response.statusText}`);
                return;
            }

            const json = await response.json();

            if (json && json.data) {
                this.currentData = json.data.data;
                console.log("✅ Heatmap API Data:", this.currentData);
            } else {
                console.warn("⚠️ No valid data returned from heatmap API");
                this.currentData = [];
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);

        } catch (error) {
            console.error("❌ Error fetching heatmap data:", error);
        }
    }

    private updateDisplay(): void {
        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.renderAnalystRating();
    }

    private renderAnalystRating(): void {
        const container = this.shadowRoot.querySelector('#heatMapContainer');
        if (!container) return;

        if (isEmptyWidgetData(this.currentData) || this.formatApiData(this.currentData).length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const html = `
        <div class="heatmapMain" data-testing-id="heatmap-main">
          <div class="chart" id="heatmapChart" style="height: 100%; width: 100%;" data-testing-id="heatmap-chart"></div>
        </div>
        `;
        container.innerHTML = html;
        this.drawChart();
    }

    private formatApiData(apiData: any): [number, number, number][] {
        const data: [number, number, number][] = [];
        if (!apiData || !Array.isArray(apiData)) return data;

        if (apiData.length > 0 && apiData[0].type === 'heatmap' && Array.isArray(apiData[0].z)) {
            const z = apiData[0].z;
            for (let i = 0; i < z.length; i++) {
                for (let j = 0; j < z[i].length; j++) {
                    data.push([j, i, z[i][j]]);
                }
            }
            return data;
        }
        return data;
    }

    private drawChart(): void {
        const chartDv = this.shadowRoot.querySelector('.chartDv');
        chartDv?.removeAttribute('data-testing-id');
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#heatmapChart') as HTMLElement;
        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);


        const isBlackTheme = this.config?.theme === 'black';
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                position: 'top',
                formatter: (params: any) => {
                    return `${this.yAxisLabels[params.data[1]]} vs ${this.xAxisLabels[params.data[0]]}<br/>Correlation: ${params.data[2].toFixed(3)}`;
                },
                backgroundColor: bgColor,
                textStyle: {
                    color: textColor
                },
                borderColor: axisLineColor,
                borderWidth: 1
            },
            grid: { left: 10, right: 60, top: 0, bottom: 0, containLabel: true },
            xAxis: {
                type: 'category',
                data: this.xAxisLabels,
                splitArea: { show: false },
                axisLabel: {
                    fontSize: fontSize,
                    color: textColor,
                    rotate: 0
                },
                axisTick: { show: false },
                axisLine: { show: false }
            },
            yAxis: {
                type: 'category',
                data: this.yAxisLabels.slice().reverse(),
                splitArea: { show: false },
                axisLabel: {
                    fontSize: fontSize,
                    color: textColor
                },
                axisTick: { show: false },
                axisLine: { show: false }
            },
            visualMap: {
                min: -0.4,
                max: 1.0,
                calculable: true,
                orient: 'vertical',
                right: 0,
                top: 0,
                bottom: 0,
                itemWidth: 20,
                itemHeight: 330,
                inRange: {
                    color: [
                        '#57307A', // Blackest purple/blue for -0.4 (approx)
                        '#1F77B4', // Blue/Teal (mid-range negative/low positive)
                        '#2CA02C', // Green
                        '#D62728', // Yellow-Green (not present in the image but common for 0.4-0.6)
                        '#FFEC00', // Bright Yellow for high positive (around 0.8-1.0)
                    ]
                },
                textStyle: { fontSize: fontSize, color: textColor },
                show: true,
                align: 'left'
            },
            series: [{
                name: 'Correlation',
                type: 'heatmap',
                data: this.formatApiData(this.currentData),
                label: { show: false },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }]
        };

        this.currentChart = echarts.init(container, this.config.theme === 'black' ? 'dark' : 'light'); // Pass theme to init
        this.currentChart.setOption(option);
        
        // Add data-testing-id to chart after render
        setTimeout(() => {
            const chartCanvas = this.shadowRoot.querySelector('#heatmapChart canvas');
            if (chartCanvas) {
                chartCanvas.setAttribute('data-testing-id', 'heatmap-chart-canvas');
            }
        }, 500);

        this.currentChart.resize();
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
        }
        this.widgetResizeObserver = new ResizeObserver(() => {
            if (this.currentChart) this.currentChart.resize();
        });
       
        chartDv?.setAttribute(
    'data-testing-id',
    'market-sector-correlation-chartdv'
);
        this.widgetResizeObserver.observe(container.parentElement!);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketSectorCorrelation: typeof TraderverseMarketSectorCorrelation; } }
if (typeof window !== 'undefined') { window.TraderverseMarketSectorCorrelation = TraderverseMarketSectorCorrelation; }